import React, { useEffect, useState, useRef } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { VideoPlayer } from './VideoPlayer';
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getAppointmentToken } from '../../pages/appointments/redux/thunk';
import { selectAppointmentToken } from '../../pages/appointments/redux/slice';
import Controls from './controls';

const client = AgoraRTC.createClient({
  mode: 'rtc',
  codec: 'vp8',
});

function Agoraa({ setShowCancelModal }) {
  const dispatch = useAppDispatch();
  const params = useParams();

  const [localCameraEnabled, setLocalCameraEnabled] = useState(true);
  const [localMicEnabled, setLocalMicEnabled] = useState(true);
  const [videoQuality, setVideoQuality] = useState(null);
  const [config, setConfig] = useState({
    token: "",
    channel: "",
    mode: 'rtc',
    codec: 'vp8',
    appId: 'e00a7e089f7145e89fb4959caf816626',
    role: 'host'
  });
  const [users, setUsers] = useState([]);
  const [localTracks, setLocalTracks] = useState([]);

  // Use a ref to always hold the latest tracks for cleanup
  const localTracksRef = useRef([]);

  // Fetch appointment token
  useEffect(() => {
    dispatch(getAppointmentToken(params?.id));
  }, [params]);

  const appointmentToken = useAppSelector(selectAppointmentToken);

  useEffect(() => {
    if (appointmentToken.data?.agora_token) {
      setConfig({
        token: appointmentToken?.data?.agora_token,
        channel: appointmentToken?.data?.channel_name,
        mode: 'rtc',
        codec: 'vp8',
        appId: 'e00a7e089f7145e89fb4959caf816626',
        role: 'host'
      });
    }
  }, [appointmentToken]);

  const handleUserJoined = async (user, mediaType) => {
    await client.subscribe(user, mediaType);

    if (mediaType === 'video') {
      setUsers(prev => [...prev, user]);
    }

    if (mediaType === 'audio') {
      user.audioTrack.play();
    }
  };

  const handleUserLeft = (user) => {
    setUsers(prev => prev.filter(u => u.uid !== user.uid));
  };

  useEffect(() => {
    if (config.token && config.appId && config.channel) {

      // Event listeners
      client.on('user-published', handleUserJoined);
      client.on('user-left', handleUserLeft);
      client.on('user-unpublished', (user, mediaType) => {
        if (mediaType !== 'audio') {
          setUsers(prev => prev.filter(u => u.uid !== user.uid));
        }
      });

      client.on('network-quality', async (stats) => {
        try {
          setVideoQuality(stats);
          const videoTrack = localTracksRef.current[1];
          if (!videoTrack) return;

          if (stats.uplinkNetworkQuality >= 1 && stats.uplinkNetworkQuality <= 2) {
            await videoTrack.setEncoderConfiguration("360p_1");
          } else if (stats.uplinkNetworkQuality >= 3 && stats.uplinkNetworkQuality <= 4) {
            await videoTrack.setEncoderConfiguration("360p_3");
          } else if (stats.uplinkNetworkQuality >= 5 && stats.uplinkNetworkQuality <= 6) {
            await videoTrack.setEncoderConfiguration("240p_3");
          }
        } catch (err) {
          console.log(err);
        }
      });

      // Join channel and create tracks
      client
        .join(config.appId, config.channel, config.token, null)
        .then(uid =>
          AgoraRTC.createMicrophoneAndCameraTracks().then(tracks => [uid, tracks])
        )
        .then(([uid, tracks]) => {
          const [audioTrack, videoTrack] = tracks;
          setLocalTracks(tracks);
          localTracksRef.current = tracks; // save to ref for cleanup
          setUsers(prev => [...prev, { uid, audioTrack, videoTrack }]);
          client.publish(tracks);
        });

      // Cleanup on unmount
      return () => {
        localTracksRef.current.forEach(track => {
          track.stop();
          track.close();
        });
        client.unpublish(localTracksRef.current).then(() => client.leave());
        client.off('user-published', handleUserJoined);
        client.off('user-left', handleUserLeft);
      };
    }
  }, [config.token, config.channel]);

  return (
    <div className='rtx_video_container'>
      <div className='video_cons_hk'>
        <div className='videoContainer'>
          {users.map(user => (
            <VideoPlayer
              localTracks={localTracks}
              setVideoQuality={setVideoQuality}
              videoQuality={videoQuality}
              client={client}
              key={user.uid}
              user={user}
              users={users}
              localCameraEnabled={localCameraEnabled}
            />
          ))}
          <Controls
            client={client}
            localTracks={localTracks}
            setVideoQuality={setVideoQuality}
            videoQuality={videoQuality}
            setLocalMicEnabled={setLocalMicEnabled}
            localMicEnabled={localMicEnabled}
            setLocalCameraEnabled={setLocalCameraEnabled}
            localCameraEnabled={localCameraEnabled}
            setShowCancelModal={setShowCancelModal}
          />
        </div>
      </div>
    </div>
  );
}

export default Agoraa;
